<template>
  <div>
    <button
      style="display: none"
      type="button"
      class="btn-show-weighing-modal"
      data-bs-toggle="modal"
      data-bs-target="#modal_weighing"
    ></button>
    <button
      style="display: none"
      type="button"
      class="btn-hide-weighing-modal"
      data-bs-target="#modal_weighing"
      data-bs-dismiss="modal"
    ></button>

    <div
      class="modal fade ksk-weigh-modal"
      id="modal_weighing"
      tabindex="-1"
      role="dialog"
      aria-labelledby="modal_weighing"
      style="z-index: 10001"
      data-bs-backdrop="static"
    >
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content ksk-weigh">
          <!-- Item: image + name + $/100g, X top-right -->
          <div class="ksk-weigh__head">
            <div
              class="ksk-weigh__thumb"
              v-if="itemDetail && itemDetail.image"
            >
              <img :src="itemDetail.image" alt="Food Image" />
            </div>
            <div class="ksk-weigh__info">
              <div class="ksk-weigh__name">
                {{ itemDetail && tName(itemDetail.item_name) }}
              </div>
              <div class="ksk-weigh__rate">{{ $t("kiosk.itemCard.per100g", { price: "$" + pricePer100g }) }}</div>
              <div
                v-if="itemDetail && itemDetail.description"
                class="ksk-weigh__desc"
              >
                {{ itemDetail.description }}
              </div>
            </div>
            <button
              type="button"
              class="btn-close-modal"
              :aria-label="$t('kiosk.common.close')"
              @click="closeModal()"
            >
              <span class="material-icons-round">close</span>
            </button>
          </div>

          <!-- Step guidance -->
          <div class="ksk-weigh__step">
            <div class="ksk-weigh__step-icon">
              <span class="material-icons-round">scale</span>
            </div>
            <div class="ksk-weigh__step-text">
              <div class="ksk-weigh__step-label">{{ $t("kiosk.weighing.step1") }}</div>
              <div class="ksk-weigh__step-title">
                {{ $t("kiosk.weighing.placeFood") }}
              </div>
            </div>
            <div class="ksk-weigh__arrow" aria-hidden="true">
              <span class="material-icons-round">arrow_downward</span>
            </div>
          </div>

          <!-- Realtime weight + price -->
          <div class="ksk-weigh__readout">
            <div class="ksk-weigh__weight">
              <span class="ksk-weigh__weight-value">{{ displayWeight }}</span>
              <span class="ksk-weigh__unit">kg</span>
            </div>
            <div
              class="ksk-weigh__pill"
              :class="{ 'ksk-weigh__pill--stable': stable }"
            >
              <span class="material-icons-round">{{
                stable ? "check_circle" : "autorenew"
              }}</span>
              {{ $t(stable ? "kiosk.weighing.stable" : "kiosk.weighing.measuring") }}
            </div>
            <div class="ksk-weigh__price">
              $<span class="weighing-price-value">{{ priceText }}</span>
            </div>
          </div>

          <!-- Minimum-to-add hint when reading is valid but below minimum -->
          <div v-if="belowMinimum" class="ksk-weigh__notice ksk-weigh__notice--warn">
            <span class="material-icons-round">info</span>
            {{ $t("kiosk.weighing.minimum", { price: "$" + minAddToCart.toFixed(2) }) }}
          </div>

          <!-- Compact error if scale not reachable -->
          <div v-if="errorMessage" class="ksk-weigh__notice ksk-weigh__notice--error">
            <span class="material-icons-round">error</span>
            {{ errorMessage }}
          </div>

          <!-- Next: greyed until canProceed, still tappable (toast says why) -->
          <material-button
            class="btn-add-to-cart"
            :class="{ disable: !canProceed }"
            @click="onNext()"
          >
            {{ $t("kiosk.common.next") }}
          </material-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import MaterialButton from "@/components/MaterialButton.vue";
import { KIOSK_WEIGH_MIN_ADD_CART } from "@/config/const";
import { translatedName } from "@/i18n/menuTranslate";

const SCALE_AGENT_BASE = "http://127.0.0.1:9876";

export default {
  name: "ModalWeighing",
  components: {
    MaterialButton,
  },
  props: {
    itemDetail: {
      type: Object,
      default: () => ({}),
    },
    reloadModal: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      eventSource: null,
      weight: 0,
      stable: false,
      connected: false,
      unit: "kg",
      ageMs: 0,
      errorMessage: "",
    };
  },
  computed: {
    pricePerKg() {
      return parseFloat(this.itemDetail?.price_per_kg || 0);
    },
    lineTotal() {
      const w = parseFloat(this.weight) || 0;
      return Math.max(0, w * this.pricePerKg);
    },
    displayWeight() {
      const w = parseFloat(this.weight) || 0;
      return w.toFixed(3);
    },
    priceText() {
      return this.lineTotal.toFixed(2);
    },
    // Display only: the item's rate shown per 100g (price_per_kg / 10).
    pricePer100g() {
      return (this.pricePerKg / 10).toFixed(2);
    },
    minAddToCart() {
      return KIOSK_WEIGH_MIN_ADD_CART;
    },
    // True once we have a valid, stable reading that is simply below the minimum.
    belowMinimum() {
      return (
        this.connected &&
        this.stable &&
        this.weight > 0 &&
        this.pricePerKg > 0 &&
        !this.errorMessage &&
        this.lineTotal < this.minAddToCart
      );
    },
    canProceed() {
      return (
        this.connected &&
        this.stable &&
        this.weight > 0 &&
        this.pricePerKg > 0 &&
        !this.errorMessage &&
        this.lineTotal >= this.minAddToCart
      );
    },
    // Why Next is refused, in the customer's words. A greyed-out button with no
    // explanation reads as a broken kiosk, so the button stays clickable and
    // answers with this in a red toast instead.
    blockMessage() {
      if (this.errorMessage) {
        return this.errorMessage;
      }

      if (!this.connected || !this.stable || this.weight <= 0) {
        return this.$t("kiosk.weighing.waitSettle");
      }

      if (this.pricePerKg <= 0) {
        return this.$t("kiosk.weighing.noKgPrice");
      }

      return this.$t("kiosk.weighing.minimumSentence", { price: "$" + this.minAddToCart.toFixed(2) });
    },
  },
  mounted() {
    const modalEl = document.getElementById("modal_weighing");
    if (modalEl) {
      modalEl.addEventListener("shown.bs.modal", this.startScale);
      modalEl.addEventListener("hidden.bs.modal", this.stopScale);
    }
  },
  beforeUnmount() {
    this.stopScale();
    const modalEl = document.getElementById("modal_weighing");
    if (modalEl) {
      modalEl.removeEventListener("shown.bs.modal", this.startScale);
      modalEl.removeEventListener("hidden.bs.modal", this.stopScale);
    }
  },
  watch: {
    reloadModal() {
      // reset state each time parent re-triggers the modal
      this.resetState();
    },
  },
  methods: {
    tName(source) {
      return translatedName(source);
    },
    resetState() {
      this.weight = 0;
      this.stable = false;
      this.connected = false;
      this.ageMs = 0;
      this.errorMessage = "";
    },
    async startScale() {
      this.resetState();
      // Read whatever is already on the scale right now, so a pre-placed item
      // shows its weight/price immediately (the /stream only emits on change).
      await this.fetchCurrentWeight();
      this.connectStream();
    },
    async fetchCurrentWeight() {
      try {
        const res = await fetch(`${SCALE_AGENT_BASE}/weight`);
        if (!res.ok) return;
        const payload = await res.json();
        this.weight = parseFloat(payload.weight) || 0;
        this.stable = !!payload.stable;
        this.connected = !!payload.connected;
        this.unit = payload.unit || "kg";
        this.ageMs = payload.age_ms || 0;
        this.errorMessage = "";
      } catch (err) {
        // ignore — connectStream() will keep providing realtime updates
      }
    },
    stopScale() {
      if (this.eventSource) {
        try {
          this.eventSource.close();
        } catch (e) {
          // ignore
        }
        this.eventSource = null;
      }
    },
    connectStream() {
      this.stopScale();
      try {
        const es = new EventSource(`${SCALE_AGENT_BASE}/stream`);
        this.eventSource = es;
        es.onmessage = (e) => {
          try {
            const payload = JSON.parse(e.data);
            this.weight = parseFloat(payload.weight) || 0;
            this.stable = !!payload.stable;
            this.connected = !!payload.connected;
            this.unit = payload.unit || "kg";
            this.ageMs = payload.age_ms || 0;
            this.errorMessage = "";
          } catch (err) {
            // ignore parse error
          }
        };
        es.onerror = () => {
          this.connected = false;
          this.stable = false;
          this.errorMessage =
            "Cannot connect to scale agent (http://127.0.0.1:9876). Make sure MintyScaleAgent is running.";
        };
      } catch (err) {
        this.errorMessage =
          "Cannot connect to scale agent (http://127.0.0.1:9876). Make sure MintyScaleAgent is running.";
      }
    },
    closeModal() {
      this.stopScale();
      $(".btn-hide-weighing-modal").click();
    },
    onNext() {
      if (!this.canProceed) {
        this.$emit("weighing-blocked", this.blockMessage);
        return;
      }
      const payload = {
        weight_kg: parseFloat(this.weight),
        price_per_kg: this.pricePerKg,
        line_total: this.lineTotal,
        unit: this.unit,
      };
      this.$emit("weighing-complete", payload);
      this.stopScale();
      $(".btn-hide-weighing-modal").click();
    },
    formatPrice(price) {
      return "$ " + parseFloat(price || 0).toFixed(2);
    },
  },
};
</script>

<style scoped>
/* KSK024 — P7 Weighing popup (kiosk theme 2). Kiosk-only component; tokens
   come from kiosk-theme2.css (var(--ksk-*)). */
.ksk-weigh-modal .modal-dialog {
  width: 920px;
  max-width: calc(100% - var(--ksk-space-4) * 2);
  margin: 0 auto;
  min-height: calc(var(--ksk-vh, 1vh) * 100);
}
.ksk-weigh {
  border: none;
  border-radius: var(--ksk-radius-popup);
  background: var(--ksk-surface);
  box-shadow: 0 24px 64px rgba(20, 26, 20, 0.25);
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: var(--ksk-space-3);
  max-height: calc(var(--ksk-vh, 1vh) * 92);
  overflow-y: auto;
  overflow-x: hidden;
  font-family: var(--ksk-font);
  color: var(--ksk-text);
}

/* Head: thumbnail + name + rate, X on the right */
.ksk-weigh__head {
  display: flex;
  align-items: center;
  gap: var(--ksk-space-3);
}
.ksk-weigh__thumb {
  width: 120px;
  height: 120px;
  flex-shrink: 0;
  border-radius: var(--ksk-radius-card);
  overflow: hidden;
  background: var(--ksk-surface-muted);
  box-shadow: var(--ksk-shadow-card);
}
.ksk-weigh__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ksk-weigh__info {
  flex: 1;
  min-width: 0;
}
.ksk-weigh__name {
  font-size: 36px;
  line-height: 44px;
  font-weight: 700;
  letter-spacing: -0.01em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}
.ksk-weigh__rate {
  margin-top: 4px;
  font-size: var(--ksk-price-size);
  line-height: var(--ksk-price-lh);
  font-weight: 700;
  color: var(--ksk-primary);
}
.ksk-weigh__desc {
  margin-top: 4px;
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  color: var(--ksk-text-secondary);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.btn-close-modal {
  align-self: flex-start;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  border: none;
  border-radius: 50%;
  background: var(--ksk-surface-muted);
  color: var(--ksk-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: transform 0.12s ease;
}
.btn-close-modal:active {
  transform: scale(0.92);
}
.btn-close-modal .material-icons-round {
  font-size: 36px;
}

/* Step guidance card */
.ksk-weigh__step {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: var(--ksk-space-3);
  border-radius: var(--ksk-radius-card);
  background: var(--ksk-primary-container);
}
.ksk-weigh__step-icon {
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  border-radius: 16px;
  background: var(--ksk-primary);
  color: var(--ksk-on-primary);
  display: flex;
  align-items: center;
  justify-content: center;
}
.ksk-weigh__step-icon .material-icons-round {
  font-size: 36px;
}
.ksk-weigh__step-text {
  flex: 1;
  min-width: 0;
}
.ksk-weigh__step-label {
  font-size: var(--ksk-caption-size);
  line-height: var(--ksk-caption-lh);
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ksk-primary);
}
.ksk-weigh__step-title {
  margin-top: 2px;
  font-size: var(--ksk-label-size);
  line-height: var(--ksk-label-lh);
  font-weight: 600;
}
.ksk-weigh__arrow {
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  border-radius: var(--ksk-radius-button);
  background: var(--ksk-surface);
  color: var(--ksk-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: var(--ksk-shadow-card);
}
.ksk-weigh__arrow .material-icons-round {
  font-size: 40px;
  animation: ksk-weigh-bounce 1.2s ease-in-out infinite;
}
@keyframes ksk-weigh-bounce {
  0%,
  100% {
    transform: translateY(-6px);
  }
  50% {
    transform: translateY(6px);
  }
}

/* Readout: weight 96px + status pill + live price 48px */
.ksk-weigh__readout {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--ksk-space-2);
  padding: var(--ksk-space-4) var(--ksk-space-3);
  border-radius: 28px;
  background: var(--ksk-surface-muted);
  text-align: center;
}
.ksk-weigh__weight {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 12px;
}
.ksk-weigh__weight-value {
  font-size: 96px;
  line-height: 96px;
  font-weight: 800;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}
.ksk-weigh__unit {
  font-size: var(--ksk-headline-size);
  line-height: var(--ksk-headline-lh);
  font-weight: 600;
  color: var(--ksk-text-secondary);
}
.ksk-weigh__pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 24px;
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-border);
  color: var(--ksk-text-secondary);
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
}
.ksk-weigh__pill .material-icons-round {
  font-size: 24px;
  animation: ksk-weigh-spin 1.2s linear infinite;
}
.ksk-weigh__pill--stable {
  background: var(--ksk-primary);
  color: var(--ksk-on-primary);
}
.ksk-weigh__pill--stable .material-icons-round {
  animation: none;
}
@keyframes ksk-weigh-spin {
  to {
    transform: rotate(360deg);
  }
}
.ksk-weigh__price {
  margin-top: var(--ksk-space-1);
  font-size: 48px;
  line-height: 56px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: var(--ksk-primary);
  font-variant-numeric: tabular-nums;
}

/* Warning (below minimum) / error (scale not reachable) */
.ksk-weigh__notice {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: var(--ksk-space-2) 20px;
  border-radius: 20px;
  font-size: var(--ksk-body-size);
  line-height: var(--ksk-body-lh);
  font-weight: 600;
}
.ksk-weigh__notice .material-icons-round {
  font-size: 28px;
  flex-shrink: 0;
}
.ksk-weigh__notice--warn {
  background: #fff4e0;
  color: #8a5800;
}
.ksk-weigh__notice--warn .material-icons-round {
  color: var(--ksk-warning);
}
.ksk-weigh__notice--error {
  background: #fdecea;
  color: var(--ksk-error);
}

/* Next — 96px, full width */
.btn-add-to-cart {
  width: 100%;
  height: 96px;
  margin-top: var(--ksk-space-1);
  border: none;
  border-radius: var(--ksk-radius-button);
  background: var(--ksk-primary) !important;
  color: var(--ksk-on-primary) !important;
  box-shadow: var(--ksk-shadow-float) !important;
  font-family: var(--ksk-font);
  font-size: 28px;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
}
.btn-add-to-cart:active {
  background: var(--ksk-primary-pressed) !important;
}
.btn-add-to-cart.disable {
  background: var(--ksk-border) !important;
  color: var(--ksk-text-secondary) !important;
  box-shadow: none !important;
  /* Deliberately still clickable (no pointer-events: none) — onNext() refuses
     the tap and raises a red toast saying why. */
}
</style>
